import { FormField } from "@common";
import {
  normalizeOtherDeductionMode,
  OTHER_DEDUCTION_MODE_AMOUNT,
} from "@utils/practitionerFormUtils";

/**
 * Renders one input per configured other-deduction tier, styled the same as the
 * other fields in `CostProductionBreakdownForm` (2-column grid of FormField).
 *
 * What the input asks for depends on the tier's mode, set on the practitioner
 * profile: an hourly tier asks for HOURS (the $/hr rate is on the profile), a
 * fixed tier asks for the dollar AMOUNT itself. The resulting deduction is
 * stored on the submitted record and displayed elsewhere (AssociateFee /
 * Journal Entry / DDS Draw tooltip / PDF).
 *
 * Props:
 *  - tiers: Array<{ label: string, mode?: string, hour_rate?: number|string }>
 *  - values: Array<string>     raw per-tier entry strings, index-aligned with `tiers`
 *  - onChange: (nextValues: Array<string>) => void
 *  - errors?: Array<string>    per-tier error message, index-aligned with `tiers`
 *  - showErrors?: boolean
 *  - disabled?: boolean
 */
export default function OtherDeductionTierInputs({
  tiers,
  values,
  onChange,
  errors = [],
  showErrors = false,
  disabled = false,
}) {
  if (!Array.isArray(tiers) || tiers.length === 0) return null;

  const handleValueChange = (idx) => (e) => {
    const next = Array.isArray(values) ? values.slice() : [];
    next[idx] = e.target.value;
    onChange(next);
  };

  return (
    <div className="mt-4 grid gap-4 md:grid-cols-2">
      {tiers.map((tier, idx) => {
        const isAmount = normalizeOtherDeductionMode(tier.mode) === OTHER_DEDUCTION_MODE_AMOUNT;
        return (
          <FormField
            key={`${tier.label}-${idx}`}
            label={`${tier.label} ${isAmount ? "($)" : "(Hours)"}`}
            placeholder=""
            type="number"
            value={values?.[idx] ?? ""}
            onChange={handleValueChange(idx)}
            error={errors[idx]}
            showError={showErrors}
            disabled={disabled}
          />
        );
      })}
    </div>
  );
}
